/* ==========================================================================
   Guide Content Component
   Shared guide content for admin and provider user guide pages.
   Renders role-appropriate sections with a sticky TOC sidebar on desktop.
   ========================================================================== */

import { GuideSection } from './GuideSection'
import { providerPayLabel } from '@/utils/paymentLabels'
import { usePortal } from '@/context/PortalContext'

type Role = 'admin' | 'provider' | 'both'

interface Section {
  id: string
  title: string
  roles: Role[]
}

const sections: Section[] = [
  { id: 'overview', title: 'Overview', roles: ['admin', 'provider'] },
  { id: 'getting-started', title: 'Getting Started', roles: ['admin', 'provider'] },
  // Admin sections
  { id: 'dashboard', title: 'Dashboard', roles: ['admin'] },
  { id: 'portal-settings', title: 'Portal Settings', roles: ['admin'] },
  { id: 'product-catalog', title: 'Product Catalog', roles: ['admin'] },
  { id: 'categories', title: 'Categories', roles: ['admin'] },
  { id: 'general-custom-fields', title: 'General Custom Fields', roles: ['admin'] },
  { id: 'provider-management', title: 'Provider Management', roles: ['admin'] },
  { id: 'access-requests', title: 'Access Requests', roles: ['admin'] },
  { id: 'prescriptions', title: 'Faxes', roles: ['admin'] },
  { id: 'team', title: 'Team & Roles', roles: ['admin'] },
  { id: 'billing', title: 'Billing & Subscription', roles: ['admin'] },
  // Provider sections
  { id: 'accepting-invitation', title: 'Accepting Your Invitation', roles: ['provider'] },
  { id: 'completing-profile', title: 'Completing Your Profile', roles: ['provider'] },
  { id: 'requesting-access', title: 'Requesting Access', roles: ['provider'] },
  { id: 'submitting-prescription', title: 'Submitting a Prescription', roles: ['provider'] },
  { id: 'saved-scripts', title: 'Saved Scripts', roles: ['provider'] },
  { id: 'prescription-history', title: 'Prescription History', roles: ['provider'] },
  { id: 'catalog', title: 'Browsing the Catalog', roles: ['provider'] },
  { id: 'password-management', title: 'Password Management', roles: ['provider'] },
  { id: 'two-factor', title: 'Two-Factor Authentication', roles: ['provider'] },
  // Shared
  { id: 'receiving-prescriptions', title: 'Receiving Prescriptions', roles: ['admin'] },
  { id: 'security', title: 'Security & Privacy', roles: ['admin', 'provider'] },
  { id: 'faq', title: 'FAQ', roles: ['admin', 'provider'] },
]

function scrollToSection(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
}

/* --------------------------------------------------------------------------
   Reusable styled sub-components
   -------------------------------------------------------------------------- */

function Table({
  headers,
  rows,
}: {
  headers: string[]
  rows: string[][]
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border border-gray-200 rounded">
        <thead>
          <tr className="bg-gray-50">
            {headers.map((h) => (
              <th
                key={h}
                className="text-left px-3 py-2 font-medium text-charcoal border-b border-gray-200"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className={i % 2 === 1 ? 'bg-gray-50/50' : ''}>
              {row.map((cell, j) => (
                <td
                  key={j}
                  className="px-3 py-2 border-b border-gray-100 text-charcoal"
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function Heading({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="font-serif text-lg text-charcoal mt-2">{children}</h3>
  )
}

function BulletList({ items }: { items: React.ReactNode[] }) {
  return (
    <ul className="list-disc list-inside space-y-1.5 text-charcoal">
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  )
}

/** Marks a section (or part of one) that only Admins and Managers can reach. */
function RoleNote({ children }: { children?: React.ReactNode }) {
  return (
    <p className="text-sm text-gray-500 italic">
      {children ?? 'Available to Admins and Managers. See Team & Roles.'}
    </p>
  )
}

/* --------------------------------------------------------------------------
   Section content components
   -------------------------------------------------------------------------- */

function OverviewSection() {
  return (
    <>
      <p>
        The TJM Provider Portal is a white-labeled web application that lets
        healthcare providers send prescription requests directly to your
        compounding pharmacy through a simple online form. The portal generates
        structured, machine-readable faxes for automated processing.
      </p>
      <Heading>How it works</Heading>
      <ol className="list-decimal list-inside space-y-1.5">
        <li>Your pharmacy configures the portal — branding, product catalog, and settings.</li>
        <li>You invite providers (or they request access themselves).</li>
        <li>Providers log in, select a patient and product, and submit.</li>
        <li>The system generates a professional PDF and faxes it to your pharmacy.</li>
        <li>You receive and process prescriptions from your normal fax queue.</li>
      </ol>
      <p>The portal supports both <strong>human</strong> and <strong>veterinary</strong> prescriptions.</p>
      <p>
        The portal changes over time. <strong>What&apos;s New</strong> lists
        what each update brought, with a dot beside it when there&apos;s
        something you haven&apos;t read yet.
      </p>
    </>
  )
}

function GettingStartedSection({ role }: { role: Role }) {
  if (role === 'provider') {
    return (
      <>
        <Heading>Receiving Your Invitation</Heading>
        <p>
          Your pharmacy will send an email invitation to create your provider account.
          The email contains a link and instructions to set your password.
        </p>
        <ol className="list-decimal list-inside space-y-1.5">
          <li>Check your inbox (and spam folder) for the invitation email.</li>
          <li>Click the link in the email.</li>
          <li>Create a secure password — the form lists its requirements as you type.</li>
          <li>Log in and complete your profile before prescribing.</li>
        </ol>
      </>
    )
  }
  return (
    <>
      <Heading>Receiving Your Invitation</Heading>
      <p>
        TJM Labs will send an email invitation to create your pharmacy admin account.
        The email contains a link and instructions to set your password. Once your
        pharmacy is set up, your own Admins and Managers can invite further
        colleagues from the <strong>Team</strong> page — see{' '}
        <strong>Team &amp; Roles</strong>.
      </p>
      <ol className="list-decimal list-inside space-y-1.5">
        <li>Check your inbox (and spam folder) for the invitation from TJM Labs.</li>
        <li>Click the link in the email.</li>
        <li>Create a secure password — the form lists its requirements as you type.</li>
        <li>Log in to your admin dashboard.</li>
      </ol>
      <p>
        The invitation link is valid for <strong>7 days</strong>. If it has
        expired, ask whoever invited you to send a new one.
      </p>
      <Heading>Logging In</Heading>
      <p>
        Visit your portal URL and navigate to the admin login page. Enter your email
        and password. If you&apos;ve forgotten your password, use the &ldquo;Forgot
        Password&rdquo; link to receive a reset email.
      </p>
      <p>
        If your email has an account at more than one pharmacy, each
        pharmacy&apos;s admin portal keeps its own sign-in. You can stay signed in
        to several of them in the same browser, and signing out of one
        doesn&apos;t sign you out of the others.
      </p>
      <p>
        The main sign-in page (the one without your pharmacy&apos;s link) has an{' '}
        <strong>Admin | Provider</strong> switch at the top. It opens on{' '}
        <strong>Admin</strong>. Providers who don&apos;t have your portal link can
        choose <strong>Provider</strong> there to sign in, and they land in your
        portal.
      </p>
      <Heading>Two-Factor Authentication</Heading>
      <TwoFactorContent role="admin" />
    </>
  )
}

function TwoFactorContent({ role }: { role: Role }) {
  const settingsPage = role === 'provider' ? 'your profile' : <strong>My Account</strong>
  return (
    <>
      <p>
        Two-factor authentication adds a 6-digit code from an authenticator app
        (such as Google Authenticator) to your sign-in. It&apos;s optional and
        off until you turn it on.
        {role === 'provider' &&
          ' It appears on your profile once you\'ve completed it for the first time.'}
      </p>
      <ol className="list-decimal list-inside space-y-1.5">
        {role === 'provider' ? (
          <li>Open your profile with the <strong>Edit Profile</strong> button at the top of the page.</li>
        ) : (
          <li>Click <strong>My Account</strong> in the admin sidebar. Everyone on your team has it, Staff included.</li>
        )}
        <li>Under <strong>Two-factor authentication</strong>, click <strong>Turn on</strong>.</li>
        <li>
          Open your authenticator app, add a new account, and scan the QR code.
          {role === 'provider' && ' It\'s listed under your pharmacy\'s name.'} If
          you can&apos;t scan it, type the key shown under the code instead.
        </li>
        <li>Enter the 6-digit code the app shows, and click <strong>Turn on</strong>.</li>
      </ol>
      <p>
        From then on, you sign in with your password and then a code from the
        app. Tick <strong>Don&apos;t ask again on this device for 30 days</strong>{' '}
        to skip the code on a browser you trust. Codes only work if your
        phone&apos;s clock is set automatically.
      </p>
      <p>
        To turn it off, click <strong>Turn off</strong> on {settingsPage} and
        enter a current code. Any devices you asked the portal to remember are
        forgotten.
      </p>
      <BulletList
        items={[
          'After five wrong codes in a row, you have to wait before trying again. The wait starts at one minute and grows with further wrong codes, up to 15 minutes.',
          'Resetting a forgotten password doesn\'t skip the code. With two-factor on, you\'re asked for a code before you\'re signed in.',
          'If your session expires, you re-enter your password and then a code, and you return to where you were.',
          ...(role === 'provider'
            ? ['Two-factor is set up separately at each pharmacy you prescribe for.']
            : []),
          role === 'provider' ? (
            <><strong>Lost your phone?</strong> Contact your pharmacy. An Admin or Manager there can reset your two-factor, and you then sign in with just your password and can turn it on again.</>
          ) : (
            <><strong>Lost your phone?</strong> Ask an Admin or Manager on your team to reset your two-factor from the <strong>Team</strong> page. You then sign in with just your password and can turn it on again from <strong>My Account</strong>.</>
          ),
        ]}
      />
    </>
  )
}

function DashboardSection() {
  return (
    <>
      <p>After logging in, you land on your admin dashboard. It shows:</p>
      <Heading>Quick Stats</Heading>
      <BulletList
        items={[
          <><strong>Total Providers</strong> — with how many are active</>,
          <><strong>Active Products</strong> — with your total product count</>,
          <><strong>Prescriptions</strong> — all time <em>(Admins and Managers only)</em></>,
        ]}
      />
      <Heading>Quick Actions</Heading>
      <BulletList
        items={[
          <><strong>Invite Provider</strong></>,
          <><strong>Add Product</strong> <em>(Admins and Managers only)</em></>,
          <><strong>Portal Settings</strong> <em>(Admins and Managers only)</em></>,
        ]}
      />
      <p>
        If you haven&apos;t added any products or providers yet, a{' '}
        <strong>Getting Started</strong> checklist guides you through the initial setup.
      </p>
    </>
  )
}

function PortalSettingsSection() {
  return (
    <>
      <p>Navigate to <strong>Portal Settings</strong> from the admin sidebar.</p>
      <RoleNote />

      <Heading>Pharmacy Information</Heading>
      <Table
        headers={['Field', 'Required', 'Notes']}
        rows={[
          ['Pharmacy Name', '—', 'Set during onboarding (read-only)'],
          ['Phone', 'Yes', 'Main contact number — the only field that blocks saving'],
          ['Fax', '—', 'Where faxes are sent (read-only, set during onboarding)'],
          ['Country', '—', 'United States (default) or Canada — see Canadian Pharmacies below'],
          ['Street Address', 'No', 'With autocomplete and address verification'],
          ['City, State, ZIP', 'No', 'Auto-filled from address verification (Province and Postal Code for Canada)'],
          ['NPI', 'No', '10-digit National Provider Identifier (not shown for Canada)'],
          ['State License', 'No', 'Your pharmacy license number (Provincial License for Canada)'],
        ]}
      />
      <p className="text-sm text-gray-600">
        The address is all-or-nothing: leave it empty and you can still save, but
        if you fill in any part of it, the rest becomes required.
      </p>

      <Heading>Canadian Pharmacies</Heading>
      <p>
        Set <strong>Country</strong> to <strong>Canada</strong> and the whole
        portal switches over for your team and your providers:
      </p>
      <BulletList
        items={[
          <>Addresses use <strong>Province</strong> and <strong>Postal Code</strong> (A1A 1A1) instead of State and ZIP — on your pharmacy, on provider practice addresses, and on patient addresses.</>,
          <><strong>NPI and DEA are not collected</strong> from providers — they disappear from the provider profile, the Add Provider form, the Providers list, Override Settings, the Request Access form and the Access Requests list. The <strong>Provider Settings</strong> button (NPI/DEA requirements) is hidden.</>,
          <>The provider&apos;s license field is labelled <strong>License Province</strong>.</>,
          <>Products use a <strong>DIN</strong> (8–11 digits) instead of an NDC. The fax prints <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm">DIN:</code> in place of <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm">NDC:</code>, and the CSV template uses a <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm">din</code> column.</>,
        ]}
      />

      <Heading>Branding</Heading>
      <p>Customize how the portal looks for your providers:</p>
      <BulletList
        items={[
          <><strong>Pharmacy Logo</strong> — Upload a PNG, JPG, GIF, or WebP image (max 5 MB). Used in your portal header.</>,
          <><strong>Primary Color</strong> — Your main brand color (hex value). Used for buttons and headers.</>,
          <><strong>Secondary Color</strong> — Your accent color (hex value). Used for highlights and focus states.</>,
        ]}
      />
      <p>Changes appear immediately for providers.</p>

      <Heading>Portal Configuration</Heading>
      <BulletList
        items={[
          <><strong>Prescribing Mode</strong> — Choose Human Only, Veterinary Only, or Both. Controls whether providers see human patient fields, animal patient fields, or both.</>,
          <><strong>Require Provider Signature</strong> — When enabled, providers must draw an electronic signature before submitting. When disabled, the signature pad still appears but signing is optional. Any signature appears on the faxed PDF.</>,
          <><strong>Enable Landing Page</strong> — When enabled, your portal has a public page displaying your catalog and an access request form. Two sub-toggles let you hide each section independently while keeping the landing page itself live: <strong>Show Catalog on Landing Page</strong> and <strong>Show Request Access Form</strong>. Both default to on, and each works on its own — you can publish the catalog without the request form, or the other way round.</>,
          <><strong>Manage Provider Addresses</strong> — When enabled, providers can&apos;t add, edit, or delete their own practice addresses. Your team manages them from the <strong>Practice Addresses</strong> action on the Providers page instead. Off by default.</>,
          <><strong>Add Country field to provider address</strong> — For US pharmacies with some prescribers abroad. When enabled, the practice-address editor shows an ISO-3166 country combobox. Non-US countries switch the state input to free text so international regions (e.g., &ldquo;Ontario&rdquo;) can be captured. US addresses continue to use the 2-letter state dropdown. Off by default. (If your pharmacy is in Canada, set <strong>Country</strong> under Pharmacy Information instead.)</>,
          <><strong>Require Patient Address</strong> — When enabled, providers must enter a complete patient address before they can submit a prescription for a <strong>human</strong> patient. Off by default, so patient addresses stay optional until you turn it on. This is a pharmacy-wide default; you can override it for individual providers on their <strong>Override Settings</strong> page.</>,
          <><strong>Phone mandatory for provider</strong> — When enabled, providers must enter a phone number before their profile is complete, so they can&apos;t prescribe until they add one. Off by default. This is a pharmacy-wide default; you can override it for individual providers on their <strong>Override Settings</strong> page.</>,
          <><strong>Hide Machine-Readable Zone on Faxes</strong> — When enabled, new prescription faxes are sent without the Machine-Readable Zone at the bottom, which makes them shorter. Off by default. Leave it off if your faxes are processed automatically. Faxes sent before you turn it on are not changed.</>,
          <><strong>Hide Payment &amp; Delivery Section</strong> — Drop the prescription-form fields your pharmacy doesn&apos;t use. A master toggle hides the entire Payment &amp; Delivery section, plus three sub-toggles hide individual fields: <strong>Hide Diagnosis Code (ICD-10)</strong>, <strong>Hide Payment Type</strong>, and <strong>Hide Delivery Method</strong>. All off by default. Turning the master toggle on hides all three fields and locks the sub-toggles; turning it off makes them editable so you can hide fields one at a time. Hidden fields don&apos;t appear on the provider&apos;s form and are left blank on the faxed prescription.</>,
        ]}
      />

      <Heading>Provider Credential Requirements</Heading>
      <p>
        Which credentials a provider must supply before they can prescribe is set
        on the <strong>Providers</strong> page rather than here — click{' '}
        <strong>Provider Settings</strong> at the top of that page to toggle{' '}
        <strong>NPI Required</strong> and <strong>DEA Required</strong>.
      </p>
      <p>
        By default NPI is required and DEA is optional. You can require either,
        both, or neither — a veterinary compounding pharmacy can safely turn both
        off. These are pharmacy-wide defaults, and you can override them for one
        provider on their <strong>Override Settings</strong> page. Canadian
        pharmacies don&apos;t collect NPI or DEA, so this button isn&apos;t shown
        for them. Requiring a <strong>phone number</strong> is a separate switch,
        under Portal Configuration above.
      </p>

      <Heading>Pharmacy Notes</Heading>
      <p>
        Use the <strong>Pharmacy Notes</strong> card to publish rules, regulations,
        reminders or anything else your providers should read. The editor supports
        bold, italic, headings, bulleted and numbered lists and links, with an
        Edit/Preview switch, up to 10,000 characters. Click{' '}
        <strong>Save Settings</strong> to publish.
      </p>
      <p>
        Providers see the note on a <strong>Pharmacy Notes</strong> tab in their
        portal. Leave the note empty and the tab doesn&apos;t appear at all.
      </p>

      <Heading>Portal URL &amp; Sharing</Heading>
      <p>
        After setup, your portal has a unique address like{' '}
        <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm">portal.tjmlabs.com/rx/your-pharmacy-name</code>.
        Use the copy button on the settings page to share it with providers.
      </p>
    </>
  )
}

function ProductCatalogSection() {
  return (
    <>
      <p>
        The catalog is the heart of the portal. Each product is a pre-configured
        prescription that providers select with minimal effort. Navigate to{' '}
        <strong>Product Catalog</strong> from the admin sidebar.
      </p>
      <RoleNote>
        Everyone on your team can view the catalog. Adding, editing, importing
        and deleting products are available to Admins and Managers — see Team
        &amp; Roles.
      </RoleNote>
      <p>
        Products are listed by drug name, then by strength in numeric order
        (2.5 mg before 10 mg), with the strength shown next to each name. Use
        the search box to find a product by name, code, NDC/DIN, strength or
        category, and tick <strong>Show inactive</strong> to include deactivated
        products in the list.
      </p>

      <Heading>Adding a Product</Heading>
      <p>Click <strong>Add Product</strong> and fill in the form:</p>

      <p className="font-medium text-charcoal mt-3">Basic Information</p>
      <Table
        headers={['Field', 'Required', 'Description']}
        rows={[
          ['Product Name', 'Yes', 'How it appears to providers (e.g., "Testosterone Cypionate 200mg/mL")'],
          ['Product Code', 'Yes', 'Your internal SKU or formula ID'],
          ['NDC / DIN', 'No', 'National Drug Code (DIN for Canadian pharmacies) — most custom compounds won\'t have one. In CSV files the column is "ndc" (or "din" for Canadian pharmacies)'],
          ['Categories', 'No', 'Group similar products — a product can have several. Type to search, or type a new name to create one inline; manage the list on the Categories page.'],
          ['Ingredients', 'No', 'For compound formulations'],
        ]}
      />

      <p className="font-medium text-charcoal mt-3">Dosing &amp; Directions</p>
      <Table
        headers={['Field', 'Required', 'Description']}
        rows={[
          ['Strength', 'No', 'Product strength, e.g., "200mg/mL"'],
          ['Dose Specific Directions', 'No', 'Prescriber-editable instructions'],
          ['Default Additional Directions', 'No', 'Read-only text appended to the SIG on the fax'],
        ]}
      />

      <p className="font-medium text-charcoal mt-3">Prescription Defaults</p>
      <Table
        headers={['Field', 'Required', 'Description']}
        rows={[
          ['Quantity', 'No', 'Default amount to dispense'],
          ['Unit/Amount', 'Yes', 'Free-text unit, e.g., "mL", "60g", "15ml vial". For compound units, prescribers enter a count (1–200).'],
          ['Days Supply', 'No', '1–365 days'],
          ['Refills', 'No', '0–11 refills'],
        ]}
      />
      <p>
        Providers can modify quantity, days supply, refills, and dose directions
        when submitting. Modified fields are flagged on the faxed prescription.
      </p>

      <Heading>Medical Necessity Statement</Heading>
      <p>
        A free-text field for clinical justification. When set, this statement
        appears read-only to the prescriber and is printed on the faxed PDF.
      </p>

      <Heading>Custom Fields</Heading>
      <p>
        You can add up to <strong>20 custom fields</strong> per product. These
        create additional form fields that the provider must fill in when prescribing.
      </p>
      <p>
        Need a question answered on <em>every</em> prescription regardless of product
        (a brand, a partner order reference, a delivery note)? Define it once on the{' '}
        <strong>General Custom Fields</strong> page instead — see that section below.
      </p>
      <p className="font-medium">Field types:</p>
      <BulletList
        items={[
          <><strong>Text</strong> — Free text input</>,
          <><strong>Number</strong> — Numeric input</>,
          <><strong>Date</strong> — Date picker</>,
          <><strong>Dropdown</strong> — Select from predefined options</>,
          <><strong>Checkbox</strong> — Yes/no toggle</>,
        ]}
      />
      <p>Each custom field can be marked as required or optional, with placeholder and helper text.</p>

      <Heading>Product Notes</Heading>
      <p>
        Add up to <strong>20 read-only label/value pairs</strong> per product.
        These appear to providers as informational metadata (e.g., cost, storage
        instructions, manufacturer).
      </p>

      <Heading>Editing &amp; Deactivating Products</Heading>
      <BulletList
        items={[
          <><strong>Edit</strong> — Click any product to modify its details. Changes take effect immediately.</>,
          <><strong>Duplicate</strong> — Create a new product pre-filled from an existing one, copying every field except the Product Name and Product Code (which you enter fresh). The original product is left unchanged, and the new product must use a unique Product Code — a duplicate code is rejected. Available from the product&apos;s action menu on desktop and the product card on mobile.</>,
          <><strong>Deactivate</strong> — Hide a product from providers. Can be reactivated later.</>,
          <><strong>Delete</strong> — Permanently removes a product. Cannot be undone.</>,
        ]}
      />
      <p>You must have at least one active product for providers to submit prescriptions.</p>

      <Heading>Deleting Several Products at Once</Heading>
      <ol className="list-decimal list-inside space-y-1.5">
        <li>Click <strong>Select</strong> next to the search box. A checkbox appears on each product.</li>
        <li>Tick the products to delete. Your selection is kept as you move between pages.</li>
        <li>Or click <strong>Select all</strong> to pick every product that matches your current search and filters, across all pages, not just the page you&apos;re on. Search first to narrow it down, for example to one drug or one category.</li>
        <li>Click <strong>Delete selected</strong>. The confirmation lists every product you picked. Click <strong>Delete Permanently</strong> to confirm, or <strong>Cancel</strong> to go back.</li>
      </ol>
      <p>
        Changing the search or filters clears your selection, so a product you
        can&apos;t see is never deleted by mistake. Click <strong>Cancel</strong>{' '}
        at any time to leave select mode. This works on mobile too.
      </p>
      <p>
        As with a single delete, it can&apos;t be undone, and existing
        prescriptions are kept but no longer linked to the product. You
        can&apos;t delete every active product at once: keep at least one
        active, or add a new product first.
      </p>

      <Heading>CSV Bulk Import</Heading>
      <ol className="list-decimal list-inside space-y-1.5">
        <li>Click <strong>Import CSV</strong> on the catalog page.</li>
        <li>Click <strong>Download CSV Template</strong> to get the correct CSV format.</li>
        <li>Fill in your products (one per row).</li>
        <li>Upload the CSV file (max 5 MB).</li>
        <li>Review results — valid rows are imported; errors are listed for invalid rows.</li>
      </ol>
      <p>
        Leave <strong>Update existing products</strong> unchecked for a plain
        import. A product code that already exists is reported as a duplicate
        and skipped, exactly as before.
      </p>
      <p className="font-medium text-charcoal mt-3">Categories in the CSV</p>
      <p>
        The category cell can hold <strong>several categories at once</strong> —
        separate them with{' '}
        <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm">/</code> or{' '}
        <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm">;</code>{' '}
        (e.g. <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm">Hormone Therapy / Injectables</code>).
        Any category name that doesn&apos;t exist yet is created automatically
        and appears on your Categories page.
      </p>
      <p>
        Beyond the standard columns in the template, you can add two kinds of
        prefixed columns to attach <strong>product notes</strong> and{' '}
        <strong>custom fields</strong> to each imported product:
      </p>
      <p className="font-medium text-charcoal mt-3">Product notes ({'note:<Label>'})</p>
      <p>
        Any column whose header starts with{' '}
        <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm">note:</code>{' '}
        becomes a read-only product note. The label after the colon is the note
        label; the cell value is what providers see. For example, a column
        named{' '}
        <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm">note:Cost</code>{' '}
        with a cell value of{' '}
        <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm">$45.00</code>{' '}
        adds a &ldquo;Cost: $45.00&rdquo; note to that product.
      </p>
      <p className="font-medium text-charcoal mt-3">Custom fields ({'field:<type>:<Label>'})</p>
      <p>
        Any column whose header starts with{' '}
        <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm">field:</code>{' '}
        defines a custom prescription field that providers fill in when
        prescribing the product. The header format is{' '}
        <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm">field:&lt;type&gt;:&lt;Label&gt;</code>,
        with an optional{' '}
        <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm">:required</code>{' '}
        suffix to make the field required.
      </p>
      <BulletList
        items={[
          <>Valid <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm">&lt;type&gt;</code> values: <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm">text</code>, <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm">number</code>, <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm">date</code>, <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm">dropdown</code>, <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm">checkbox</code>.</>,
          <>For <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm">dropdown</code> columns, set the cell value to the pipe-separated option list — e.g. <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm">Small|Medium|Large</code>.</>,
          <>For <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm">text</code>, <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm">number</code>, <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm">date</code>, and <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm">checkbox</code>, any non-empty cell value attaches the field to that product. Leave the cell blank to skip the field for that row.</>,
          <>Append <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm">:required</code> to the header to mark the field required — e.g. <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm">field:text:Lot Number:required</code>.</>,
          <>Up to <strong>20 custom field columns</strong> per CSV. Two columns can&apos;t define the same field label.</>,
        ]}
      />
      <p>Example header row combining a standard column, a note, and two custom fields:</p>
      <pre className="bg-gray-100 rounded p-3 text-xs text-charcoal overflow-x-auto">
        <code>{'display_name*,product_code*,quantity_unit*,note:Cost,field:text:Lot Number:required,field:dropdown:Strength'}</code>
      </pre>

      <Heading>Bulk Editing Your Catalog (CSV)</Heading>
      <p>
        Beyond importing new products, you can edit your <strong>existing</strong>{' '}
        catalog in a spreadsheet and upload the changes back.
      </p>
      <ol className="list-decimal list-inside space-y-1.5">
        <li>Click <strong>Import CSV</strong> on the catalog page.</li>
        <li><strong>Download Catalog</strong> exports every product you have, in the same format as the import template (including your <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm">note:</code> and <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm">field:</code> columns and a read-only status column).</li>
        <li>Edit the file in Excel, Google Sheets, or any spreadsheet tool — prices, directions, quantities, refills, categories. You can also add brand-new rows.</li>
        <li>Tick <strong>Update existing products</strong> and upload. Rows whose product code matches an existing product apply as <strong>edits</strong> instead of being flagged as duplicates; rows with a new product code <strong>create</strong> new products.</li>
        <li>Review the <strong>preview</strong> — nothing has been saved yet. It shows every product that will change with its old and new value side by side, any value that <strong>will be cleared</strong>, the products and categories that will be created, and how many rows are identical to what you already have.</li>
        <li>Click <strong>Apply Changes</strong> to commit, or navigate away to cancel. Nothing is written until you press the button.</li>
      </ol>
      <p className="font-medium text-charcoal mt-3">
        Important — in edit mode the file is the source of truth.
      </p>
      <p>
        An empty cell <strong>clears</strong> that field on the matched product.
        Always start from <strong>Download Catalog</strong> so you&apos;re editing
        your current values rather than a partially-filled sheet, and read the
        preview before applying.
      </p>
      <p>A few guardrails make this safe:</p>
      <BulletList
        items={[
          <><strong>Trimmed files are rejected.</strong> In edit mode the upload must contain the full set of standard columns. If you delete columns (e.g. keep only product code and name for a bulk rename), the file is rejected with an error naming the missing columns — it will never silently blank the fields you removed.</>,
          <><strong>Notes and custom fields are left alone when their columns are absent.</strong> If your file has no <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm">note:</code> or <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm">field:</code> columns at all, existing product notes and custom fields are preserved untouched.</>,
          <><strong>Unchanged rows are skipped.</strong> Re-uploading an unedited export reports every row as unchanged and writes nothing, so the counts reflect real edits only.</>,
          <><strong>Plain imports are unaffected.</strong> With <strong>Update existing products</strong> unchecked, import behaves exactly as it always has.</>,
        ]}
      />
    </>
  )
}

function CategoriesSection() {
  return (
    <>
      <p>
        Categories group your catalog products, both on the admin catalog list
        and in the product picker your providers use. Navigate to{' '}
        <strong>Categories</strong> from the admin sidebar (just under Product
        Catalog).
      </p>
      <RoleNote />
      <p>
        Each category shows its <strong>name</strong> and a{' '}
        <strong>Products</strong> count. Click either one to jump to your
        Product Catalog filtered to that category; a chip at the top of the
        catalog shows the active filter and clears it in one click.
      </p>

      <Heading>Creating a Category</Heading>
      <p>
        Type a name in the box at the top of the page (e.g. &ldquo;Hormone
        Therapy&rdquo;) and click <strong>Add Category</strong>. Names are unique
        per pharmacy and are matched without regard to capitalization, so
        &ldquo;Hormone Therapy&rdquo; and &ldquo;hormone therapy&rdquo;
        can&apos;t both exist.
      </p>
      <p>
        Categories exist independently of products — you can set up your whole
        category structure before adding a single product. You can also create
        one without leaving the product form: in the <strong>Categories</strong>{' '}
        box on Add/Edit Product, type a name that doesn&apos;t exist yet and
        choose the <strong>Create</strong> option that appears.
      </p>

      <Heading>Assigning Products to Categories</Heading>
      <BulletList
        items={[
          <><strong>From the product</strong> — open <strong>Add Product</strong> or edit an existing one and use the <strong>Categories</strong> multi-select. Start typing to filter your existing categories, click to add them as chips, and click a chip&apos;s × to remove it. A product can belong to as many categories as you need.</>,
          <><strong>From the category</strong> — open a category&apos;s action menu (⋮) and choose <strong>Manage Products</strong> to tag or untag a whole batch of products in one modal.</>,
        ]}
      />
      <p>
        The catalog table shows every category on each product, collapsing to an
        expand arrow when a product has more than fits the column.
      </p>

      <Heading>Renaming a Category</Heading>
      <p>
        Choose <strong>Rename</strong> from the category&apos;s action menu.
        Every product using that category follows the new name automatically —
        there&apos;s nothing to update product by product.
      </p>

      <Heading>Deleting a Category</Heading>
      <p>
        Choose <strong>Delete</strong> from the category&apos;s action menu. The
        confirmation tells you how many products are affected. Deleting a
        category <strong>never deletes products</strong> — they simply lose that
        one tag and stay in your catalog, and any other categories they belong
        to are untouched.
      </p>
    </>
  )
}

function GeneralCustomFieldsSection() {
  return (
    <>
      <p>
        General custom fields are questions asked once on <em>every</em>{' '}
        prescription, whatever the product — for example a brand, a partner
        order reference, or a delivery note. Navigate to{' '}
        <strong>General Custom Fields</strong> from the admin sidebar.
      </p>
      <RoleNote />

      <Heading>Setting Up Fields</Heading>
      <p>
        Add fields exactly as you would on a product: <strong>Text</strong>,{' '}
        <strong>Number</strong>, <strong>Date</strong>, <strong>Dropdown</strong>{' '}
        or <strong>Checkbox</strong>, each required or optional, with placeholder
        and helper text — up to <strong>20 fields</strong>. Click{' '}
        <strong>Save changes</strong> when you&apos;re done. Remove every field
        and save to stop asking.
      </p>
      <p>
        Each field&apos;s key (shown under its label) must be unique, and
        can&apos;t be the same as a custom field key on any of your products,
        because both print on the fax the same way. The portal tells you if a
        key clashes, whether you&apos;re saving this page, a product, or a CSV
        import.
      </p>
      <p>
        A field can optionally be limited to specific providers: expand it and
        set <strong>Visible to</strong> to <em>Selected providers</em>, then
        pick them — the picker shows each provider&apos;s name and clinic.{' '}
        <em>All providers</em> (the default) asks everyone; <em>No one</em>{' '}
        parks the field without deleting it. If you delete a provider, they are
        removed from every field automatically; a field that was assigned only
        to them switches to <em>No one</em> rather than to everyone, so a
        required field never starts blocking other providers unexpectedly. You
        can change the assignment any time by expanding the field again and
        saving.
      </p>

      <Heading>What Providers See</Heading>
      <p>
        An <strong>Additional Information</strong> card at the end of the New
        Prescription page, after the products and before Payment &amp; Delivery.
        It&apos;s asked once per submission and applies to every product in it;
        a provider only sees the fields that apply to them. Required fields
        must be filled in before the prescription can be submitted.
      </p>

      <Heading>On the Fax</Heading>
      <p>
        Answers print under <strong>Additional Information</strong>, after any
        product custom field answers, and in the machine-readable zone as{' '}
        <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm">CUSTOM_&lt;KEY&gt;</code>{' '}
        lines. When one submission includes several products, every fax carries
        the same answers.
      </p>
    </>
  )
}

function ProviderManagementSection() {
  return (
    <>
      <p>Navigate to <strong>Providers</strong> from the admin sidebar.</p>

      <Heading>Inviting a Provider</Heading>
      <p>Click <strong>Add Provider</strong> and choose one of two modes:</p>
      <BulletList
        items={[
          <><strong>Email Invitation</strong> — Enter the provider&apos;s email. They receive an invitation to create their own password.</>,
          <><strong>Create with Password</strong> — Enter email and a password. The account is immediately active. The provider is required to change this pharmacy-set password the first time they log in before they can use the portal, so the temporary password you shared can&apos;t be reused to prescribe. Providers you invite by email are unaffected.</>,
        ]}
      />

      <Heading>Provider List</Heading>
      <p>View all providers in a searchable, filterable table:</p>
      <BulletList
        items={[
          'Search by name, email, NPI, or primary clinic name',
          'Filter by status: Active, Invited, or Disabled',
          'See name, email, NPI, license, status, last login, and custom catalog badge',
          <>A <strong>Clinic</strong> column shows each provider&apos;s primary practice-address clinic name, with a &ldquo;+N more addresses&rdquo; hint when they practice at several locations (or &ldquo;No clinic&rdquo; when none is on file)</>,
        ]}
      />

      <Heading>Provider Details</Heading>
      <p>
        Click a provider&apos;s row (or choose <strong>View Details</strong> from
        the row menu) to open everything you have on file for them:
      </p>
      <BulletList
        items={[
          <><strong>Contact</strong> — email, phone, and fax</>,
          <><strong>Credentials</strong> — credential type, NPI, DEA number, license number and state, supervising physician (for PAs and NPs), and whether they&apos;re marked as a veterinarian. Canadian pharmacies don&apos;t see NPI or DEA.</>,
          <><strong>Practice Addresses</strong> — every office on file, with the primary address and any payment / delivery defaults</>,
          <><strong>Account</strong> — whether their profile is complete, whether they still need to replace a password you set, whether two-factor authentication is on, when their invitation expires, when they were added, and their last login</>,
          <><strong>Catalog Access</strong> — all products, or how many products are assigned to them</>,
          <><strong>Settings Overrides</strong> — which portal settings are overridden for this provider, next to your portal default</>,
        ]}
      />
      <p>
        The page is read-only. Each section has a button to the tool that changes
        it, and the provider actions below are available at the top of the page.
        Everyone on your team, including Staff, can open provider details.
      </p>
      <p>
        If a provider who uses two-factor authentication loses their phone, an
        Admin or Manager can click <strong>Reset Two-Factor</strong> at the top of
        their details page. This turns two-factor off for that provider, so they
        can sign in with just their password and set it up again from their
        Profile. The button only appears when the provider has two-factor on,
        and Staff don&apos;t see it.
      </p>

      <Heading>Provider Actions</Heading>
      <BulletList
        items={[
          <><strong>View Details</strong> — Open the provider&apos;s details page (see above)</>,
          <><strong>Resend Invitation</strong> — For providers who haven&apos;t accepted yet</>,
          <><strong>Enable/Disable</strong> — Disabled providers cannot log in but their data is preserved</>,
          <><strong>Delete</strong> — Permanently removes the provider. Cannot be undone.</>,
          <><strong>Manage Catalog</strong> — Assign a custom product subset (see below)</>,
          <><strong>Practice Addresses</strong> — View, add, edit, or remove the provider&apos;s offices, and set per-address payment / delivery defaults (see below)</>,
          <><strong>Override Settings</strong> — Tailor this provider&apos;s portal settings independently of your pharmacy defaults (see below)</>,
        ]}
      />

      <Heading>Per-Provider Setting Overrides</Heading>
      <p>
        Most of the toggles under <strong>Portal Configuration</strong> are
        pharmacy-wide defaults, but you can override any of them for a{' '}
        <strong>single provider</strong>. Open a provider&apos;s{' '}
        <strong>Override Settings</strong> page from the button on their
        details page, or by choosing <strong>Override Settings</strong> from
        the row menu on the Providers list.
      </p>
      <p>Each setting offers three choices:</p>
      <BulletList
        items={[
          <><strong>Default (On)</strong> / <strong>Default (Off)</strong> — Follow your pharmacy-wide setting. This is where every row starts, and the label tells you what that default currently is.</>,
          <><strong>On</strong> / <strong>Off</strong> — Explicitly override the default for just this provider.</>,
        ]}
      />
      <p>The settings you can override per provider are:</p>
      <BulletList
        items={[
          <><strong>Require Provider Signature</strong></>,
          <><strong>NPI Required</strong></>,
          <><strong>DEA Required</strong></>,
          <><strong>Phone Required</strong></>,
          <><strong>Patient Address Required</strong> (human patients only)</>,
          <><strong>Manage Provider Addresses</strong></>,
          <><strong>Country Field</strong></>,
          <><strong>Hide Payment &amp; Delivery Section</strong>, along with its individual <strong>Hide Diagnosis Code</strong>, <strong>Hide Payment Type</strong>, and <strong>Hide Delivery Method</strong> sub-fields. As on the Settings page, the three sub-fields lock while the section itself is hidden.</>,
        ]}
      />
      <p>
        Each row also shows <strong>Currently in effect</strong>, so you can see
        the value this provider actually gets without working it out yourself.
      </p>
      <p>
        Changes save automatically. A setting left on <strong>Default</strong>{' '}
        always tracks your pharmacy-wide value, so it follows along if you later
        change it; an explicit <strong>On</strong> or <strong>Off</strong> stays
        fixed for that provider until you set it back to Default. These
        overrides sit on top of your pharmacy-wide defaults, including the
        NPI/DEA credential requirements you set with{' '}
        <strong>Provider Settings</strong>.
      </p>
      <p className="text-sm text-gray-600">
        Everyone on your team can open Override Settings, including Staff — it
        sits alongside the other provider actions. Changing your{' '}
        <em>pharmacy-wide</em> defaults on the Settings and Provider Settings
        screens is limited to Admins and Managers.
      </p>

      <Heading>Marking a Provider as a Veterinarian</Heading>
      <p>
        The same page has a <strong>Veterinarian</strong> checkbox, which is
        always available to edit.
      </p>
      <BulletList
        items={[
          <><strong>Checking it</strong> sets that provider&apos;s <strong>NPI Required</strong> and <strong>DEA Required</strong> to <strong>Off</strong> — a veterinarian has neither credential. Both rows update immediately, and because they become explicit overrides, that provider is unaffected if you later change your pharmacy-wide NPI or DEA requirement.</>,
          <><strong>Unchecking it</strong> returns both settings to <strong>Default</strong>, so the provider picks your portal defaults back up.</>,
        ]}
      />
      <p>
        This is a one-time flip in each direction. After marking someone as a
        veterinarian you can still set NPI Required or DEA Required back to On
        manually for that provider, and the Veterinarian designation stays as
        you left it.
      </p>

      <Heading>Setting Payment &amp; Delivery Defaults</Heading>
      <p>
        Payment-type and delivery-method options are set <strong>per practice
        address</strong>, not per provider. This lets a provider who bills
        differently at each clinic (e.g., provider-pay at one office,
        patient-pay at another) see the right options automatically when they
        switch offices on the prescription form.
      </p>
      <ol className="list-decimal list-inside space-y-1.5">
        <li>Click the provider&apos;s menu → <strong>Practice Addresses</strong></li>
        <li>Add or edit an address — the <strong>Prescription Defaults</strong> part of the form has a checkbox for each <strong>Payment Type</strong> and each <strong>Delivery Method</strong></li>
        <li>Leave every box unchecked to let the provider choose, check one option to lock the address to it, or check several to limit the provider to those options</li>
        <li>Save</li>
      </ol>
      <p>
        The two fields are independent — you can set one, both, or neither on
        each address. With one option checked, a provider prescribing from that
        address sees only that value on the form, marked <em>(set by
        pharmacy)</em>. With several checked, they see only those options and
        can pick one of them (or leave the field empty, since it stays
        optional). When they switch the &ldquo;Prescribing From&rdquo; picker
        to a different office, the options are re-read from that address, and
        a choice the new office doesn&apos;t offer is cleared. Addresses that
        had a single default before this change keep it as one checked option.
      </p>

      <Heading>Managing Practice Addresses</Heading>
      <p>
        Every provider must have at least one practice address on file.
        Providers manage their own addresses from the profile page by default,
        or your team can manage them on their behalf by flipping the{' '}
        <strong>Manage Provider Addresses</strong> toggle in Portal Settings.
      </p>
      <ol className="list-decimal list-inside space-y-1.5">
        <li>Click the provider&apos;s menu → <strong>Practice Addresses</strong></li>
        <li>The modal lists all of that provider&apos;s offices, with the primary address at the top</li>
      </ol>
      <p>From the modal you can:</p>
      <BulletList
        items={[
          <><strong>Add</strong> a new address — street, city, state, ZIP, plus an optional <strong>Office / Clinic Name</strong> (e.g., &ldquo;Downtown Clinic&rdquo;) to distinguish multiple locations. When the <strong>Add Country field</strong> toggle is on in Portal Settings, you&apos;ll also see a country combobox, and the state input becomes free text for non-US countries.</>,
          <>Check the <strong>Payment Type</strong> and <strong>Delivery Method</strong> options each address allows under <strong>Prescription Defaults</strong> — one to lock it, several to limit the provider to those, none to let them choose</>,
          <><strong>Promote</strong> a non-primary address to primary with the star icon — the primary address is pre-selected on the prescription form</>,
          <><strong>Edit</strong> any field with the pencil icon</>,
          <><strong>Delete</strong> an address with the trash icon — any address can go, including the primary, as long as one remains; delete the primary and another is promoted automatically</>,
        ]}
      />
      <p>
        When a provider has more than one address, the prescription form
        shows a <strong>Prescribing From</strong> picker so they can choose
        which office each script is coming from. The selection is snapshotted
        onto the prescription, so the faxed PDF always shows the office the
        provider intended — even if the address is later edited or deleted.
      </p>

      <Heading>Custom Catalog Assignments</Heading>
      <p>
        By default, every provider sees your full active catalog. To restrict a
        provider to specific products:
      </p>
      <ol className="list-decimal list-inside space-y-1.5">
        <li>Click the provider&apos;s menu → <strong>Manage Catalog</strong></li>
        <li>Select which products this provider should see</li>
        <li>Save</li>
      </ol>
      <p>
        A <strong>Custom</strong> badge appears next to the provider&apos;s name.
        To restore full catalog access, clear all assignments.
      </p>
    </>
  )
}

function AccessRequestsSection() {
  return (
    <>
      <p>
        When your <strong>Landing Page</strong> is enabled, providers can request
        access without an invitation. Navigate to <strong>Access Requests</strong>{' '}
        from the admin sidebar.
      </p>

      <p>
        While any request is waiting on you, a count appears next to{' '}
        <strong>Access Requests</strong> in the sidebar.
      </p>

      <Heading>Reviewing Requests</Heading>
      <p>
        Requests appear in a table with tabs for All, Pending, Approved, and
        Denied. The columns are name, email, NPI, license state, the provider&apos;s
        optional message, status, and the date submitted — with their phone
        number shown under the name. For a Canadian pharmacy the NPI column is
        hidden and the license column shows the province.
      </p>
      <p>
        A long message is shortened to fit its column with a chevron
        (&#8964;) beside it; click the chevron to expand the full text in place,
        and again to collapse it.
      </p>

      <Heading>Approving a Request</Heading>
      <p>
        Click <strong>Approve</strong> on a pending request. This automatically
        creates a provider account and sends an invitation email.
      </p>

      <Heading>Denying a Request</Heading>
      <p>
        Click <strong>Deny</strong> on a pending request. You can optionally
        provide a denial reason for your records.
      </p>
    </>
  )
}

function PrescriptionsSection() {
  return (
    <>
      <p>
        Navigate to <strong>Faxes</strong> from the admin sidebar to browse
        every prescription fax submitted to your pharmacy. Each row shows the
        date, reference ID, <strong>provider</strong> who sent it,{' '}
        <strong>clinic</strong> (the office or clinic name the provider
        selected at submission), patient, drug, and status.{' '}
        <strong>View Fax</strong> opens the generated PDF in a new tab — available
        for any fax that has a PDF, including ones still in Submitted.
      </p>
      <RoleNote />
      <p>
        The Clinic column is captured at submission time, so it stays accurate
        even if the provider later renames or removes that practice address.
        Older faxes, sent before this was captured, show a dash.
      </p>
      <p>
        If your pharmacy has shipment tracking, a shipped prescription shows
        its <strong>tracking number</strong> under the <strong>Shipped</strong>{' '}
        badge, with the carrier, service and ship date below it. UPS, FedEx,
        USPS and DHL numbers link to the carrier&apos;s tracking page.
      </p>
      <Heading>Filters</Heading>
      <BulletList
        items={[
          <><strong>Search</strong> — matches reference ID, drug name, patient name (human first/last, animal name, owner name), or clinic name (the office / clinic shown in the Clinic column)</>,
          <><strong>Provider</strong> dropdown — narrow to faxes from a single provider</>,
          <><strong>Date From / Date To</strong> — restrict to a window</>,
          <><strong>Status</strong> — Submitted, Fax Sent, Delivered, or Failed (some pharmacies also see <strong>Shipped</strong> once an order has been dispatched)</>,
        ]}
      />
      <p>
        Filters combine. A <strong>Clear filters</strong> button appears
        whenever any filter is active and resets everything in one click.
        When you change a filter or page, the current rows stay on screen and
        the <strong>Refresh</strong> icon spins until the new ones arrive.
      </p>

      <Heading>Retrying Faxes</Heading>
      <p>
        A <strong>Retry</strong> button appears right in the <strong>Actions</strong>{' '}
        column of the Faxes list (next to <strong>View Fax</strong>), on both
        desktop and mobile — there&apos;s no need to open a details view. It shows
        for any fax that has a generated PDF and hasn&apos;t been Delivered yet,
        including faxes left in Submitted after a temporary fax-provider outage.
      </p>
      <p>
        Clicking it resends the fax and shows a confirmation on success, or a
        clear message if the retry can&apos;t proceed (for example, the maximum
        number of attempts has been reached). The system also retries
        automatically, but manual retry is available whenever you need it.
      </p>

      <Heading>Failed Delivery Alerts</Heading>
      <p>You&apos;ll know right away when a fax doesn&apos;t go through:</p>
      <BulletList
        items={[
          <><strong>On the Faxes list</strong>, a prescription whose latest fax attempt failed shows a red <strong>&ldquo;Delivery failed &middot; attempt N&rdquo;</strong> note beneath its status badge — on both desktop and mobile — even while automatic retries are still in progress. Hover the note (or the <strong>Failed</strong> badge) to see the exact fax error. This surfaces a failed attempt immediately, instead of only once every retry is exhausted and the status flips to <strong>Failed</strong>.</>,
          <><strong>By email</strong>, every active <strong>Admin</strong> and <strong>Manager</strong> on your pharmacy team automatically receives a notification whenever a fax fails to deliver. It includes the reference ID, the prescriber, which attempt failed (e.g., &ldquo;Attempt 1 of 3&rdquo;), the error, and a link to your Faxes page — and tells you whether retries remain or the prescription has been marked failed. The email contains no patient health information. Staff don&apos;t receive it, because they don&apos;t have access to the Faxes page it links to.</>,
        ]}
      />
    </>
  )
}

function TeamSection() {
  return (
    <>
      <p>
        Navigate to <strong>Team</strong> from the admin sidebar to invite and
        manage the people who can sign in to your pharmacy&apos;s admin portal.
      </p>
      <RoleNote>Available to Admins and Managers.</RoleNote>

      <Heading>The Three Roles</Heading>
      <p>
        Everyone on your team can work with providers — inviting them, editing
        them, assigning catalogs and practice addresses, and reviewing access
        requests — and everyone can view your product catalog. Roles differ in
        what else they can reach:
      </p>
      <Table
        headers={['', 'Admin', 'Manager', 'Staff']}
        rows={[
          ['Providers, access requests, viewing the catalog', 'Yes', 'Yes', 'Yes'],
          ['Editing the catalog, CSV import, Categories, General Custom Fields', 'Yes', 'Yes', 'No'],
          ['Portal Settings and branding', 'Yes', 'Yes', 'No'],
          ['Faxes, fax retry, failed-fax emails', 'Yes', 'Yes', 'No'],
          ['Billing', 'Yes', 'Yes', 'No'],
          ['Inviting and managing Staff', 'Yes', 'Yes', 'No'],
          ['Inviting and managing Admins and Managers', 'Yes', 'No', 'No'],
        ]}
      />
      <p>
        Anything a role can&apos;t reach simply doesn&apos;t appear in their
        sidebar. A Staff member signing in sees Dashboard, Product Catalog,
        Providers, Access Requests and this guide, and nothing else.
      </p>
      <p>
        You can have up to <strong>3 Admins</strong>, <strong>10 Managers</strong>{' '}
        and <strong>10 Staff</strong>. The cards at the top of the Team page show
        how many of each you&apos;re currently using.
      </p>

      <Heading>Inviting a Teammate</Heading>
      <p>
        Click <strong>Invite</strong> and enter their name, email and role. They
        receive an email invitation to set their own password, valid for{' '}
        <strong>7 days</strong>.
      </p>
      <BulletList
        items={[
          <>New members default to <strong>Staff</strong>, so an invite sent without touching the role picker grants the least access.</>,
          <>An <strong>Admin</strong> can assign any role. A <strong>Manager</strong> can only invite Staff.</>,
          <>An email address can be used once per pharmacy. The same person can hold accounts at different pharmacies with the same address.</>,
        ]}
      />

      <Heading>Managing Teammates</Heading>
      <p>
        Each row&apos;s action menu (⋮) offers what applies to that person. You
        won&apos;t see a menu on your own row, and a Manager won&apos;t see one on
        an Admin&apos;s or another Manager&apos;s row.
      </p>
      <BulletList
        items={[
          <><strong>Resend invitation</strong> / <strong>Cancel invitation</strong> — For people who haven&apos;t accepted yet. Cancelling stops their link working and frees the email address to be invited again.</>,
          <><strong>Change to Admin / Manager / Staff</strong> — Move someone between roles.</>,
          <><strong>Disable</strong> / <strong>Re-enable</strong> — For people who have accepted. A disabled member keeps their history but can&apos;t sign in.</>,
          <><strong>Reset two-factor</strong> — For people who have two-factor authentication on and have lost access to their authenticator app. It turns two-factor off, so they sign in with just their password and can set it up again from <strong>My Account</strong>. Devices they asked the portal to remember are forgotten.</>,
        ]}
      />
      <p>A few rules the page enforces:</p>
      <BulletList
        items={[
          <>Once someone has accepted their invitation you can no longer remove them — <strong>disable</strong> them instead, so the record of what they did stays intact.</>,
          <>Someone who hasn&apos;t accepted yet can&apos;t be disabled. Resend or cancel their invitation instead.</>,
          <>Your pharmacy must always keep at least one active Admin, so the last one can&apos;t be demoted, disabled or removed.</>,
        ]}
      />

      <Heading>Role Changes Take Effect Immediately</Heading>
      <p>
        When you change someone&apos;s role or disable them, it applies at once —
        they don&apos;t keep their old access until they next sign in.
      </p>
      <BulletList
        items={[
          <>On a <strong>role change</strong>, that person is signed out wherever they&apos;re working and told their access has changed. They sign back in and pick up their new permissions.</>,
          <>On being <strong>disabled</strong>, they&apos;re signed out and told to contact their administrator.</>,
        ]}
      />
      <p className="text-sm text-gray-600">
        Worth a heads-up before you change someone&apos;s role mid-task — anything
        they hadn&apos;t saved will be lost.
      </p>
    </>
  )
}

function BillingSection() {
  return (
    <>
      <p>Navigate to <strong>Billing</strong> from the admin sidebar.</p>
      <RoleNote />

      <Heading>Subscription Overview</Heading>
      <BulletList
        items={[
          <><strong>Plan Tier</strong> — Starter, Growth, or Scale</>,
          <><strong>Status</strong> — Active, Past Due, Canceled, or Incomplete</>,
          <><strong>Billing Cycle</strong> — Monthly or Annual</>,
          <><strong>Current Period</strong> — Start and end dates</>,
        ]}
      />
      <p>
        The <strong>Plan Pricing</strong> card shows your current price — in
        CA$ if you&apos;re billed in Canadian dollars — and your next invoice
        date. If you&apos;ve scheduled a change of plan or billing cycle, a
        notice there says what it changes to and that it takes effect at the
        start of your next billing period.
      </p>

      <Heading>Usage Meter</Heading>
      <p>
        Track your prescription usage with a visual progress bar. An alert is
        sent when you reach 80% of your limit. If you exceed your limit, overage
        charges apply.
      </p>

      <Heading>Managing Your Plan</Heading>
      <p>
        Click <strong>Manage Billing</strong> to open the Stripe Customer Portal
        where you can update payment methods, change plan tier, view invoices,
        or cancel your subscription.
      </p>
    </>
  )
}

function AcceptingInvitationSection() {
  return (
    <>
      <p>When a pharmacy invites you, you receive an email with a link to set up your account.</p>
      <ol className="list-decimal list-inside space-y-1.5">
        <li>Click the link in the invitation email.</li>
        <li>Create a password — the form lists its requirements as you type.</li>
        <li>You&apos;re now logged in and ready to complete your profile.</li>
      </ol>
      <p>
        The invitation link is valid for <strong>7 days</strong>, as stated in
        the email. If it has expired, ask your pharmacy to resend the
        invitation.
      </p>
    </>
  )
}

function CompletingProfileSection() {
  return (
    <>
      <p>
        Before you can submit prescriptions, you must complete your profile.
        Open it with the <strong>Edit Profile</strong> button at the top of the
        page.
      </p>

      <p className="font-medium text-charcoal mt-3">Required Fields</p>
      <Table
        headers={['Field', 'Description']}
        rows={[
          ['Full Name', 'Your legal name as it appears on your license'],
          ['NPI', 'Your 10-digit National Provider Identifier (required by default, configurable by pharmacy)'],
          ['License Number', 'Your state (or provincial) license number'],
          ['License State', 'The state where you are licensed (License Province at Canadian pharmacies)'],
          ['At least one practice address', 'See Practice Addresses below. Not required if your pharmacy manages addresses on your behalf'],
        ]}
      />

      <p className="font-medium text-charcoal mt-3">Optional Fields</p>
      <Table
        headers={['Field', 'Description']}
        rows={[
          ['Phone', 'Your contact number (may be required by your pharmacy)'],
          ['Fax', 'Your fax number'],
          ['Credential Type', 'MD, DO, NP, PA, DDS, DMD, DPM, OD, DVM, ND, NMD, MB, or Other'],
          ['DEA Number', 'Your DEA registration number (may be required by your pharmacy)'],
          ['Supervising Physician Name', 'Optional — shown when your credential type is PA or NP'],
          ['Supervising Physician NPI', 'Optional — shown when your credential type is PA or NP. If provided, must be 10 digits and checksum-validated. Prints on the faxed prescription PDF'],
        ]}
      />
      <p className="text-sm text-gray-600">
        At a <strong>Canadian</strong> pharmacy the profile doesn&apos;t ask for
        an NPI or DEA number at all, and addresses use Province and Postal Code.
      </p>

      <Heading>Practice Addresses</Heading>
      <p>
        You can add <strong>multiple practice addresses</strong> to your
        profile — one per clinic or office you see patients at. Each address
        carries an optional <strong>Office / Clinic Name</strong> (e.g.,
        &ldquo;Downtown Clinic&rdquo;) so you can tell them apart.
      </p>
      <BulletList
        items={[
          <><strong>Add</strong> a new address — street, city, state, ZIP are required; office name is optional. If your pharmacy has enabled the country field, you can select any country (US is the default); for non-US countries, the state input accepts free text (e.g., &ldquo;Ontario&rdquo;).</>,
          <><strong>Promote</strong> an address to primary with the star icon (the primary is pre-selected on the prescription form)</>,
          <><strong>Edit</strong> any address with the pencil icon</>,
          <><strong>Delete</strong> an address you no longer use — you can delete any of them, including your primary, as long as one remains. Delete the primary and another is promoted for you</>,
        ]}
      />
      <p>
        If your pharmacy manages addresses on your behalf, the section will
        be read-only with a note explaining who to contact. In that case a
        practice address isn&apos;t required to complete your profile, so you
        can start prescribing right away.
      </p>
      <p>
        When you have more than one address, the prescription form shows a{' '}
        <strong>Prescribing From</strong> picker so you can choose the correct
        office for each script. With a single address on file, the form uses
        it automatically.
      </p>
    </>
  )
}

function RequestingAccessSection() {
  return (
    <>
      <p>If a pharmacy has their landing page enabled, you can request access without an invitation:</p>
      <ol className="list-decimal list-inside space-y-1.5">
        <li>Visit the pharmacy&apos;s portal URL.</li>
        <li>Browse the product catalog on the landing page.</li>
        <li>Fill out the <strong>Request Access</strong> form with your name, email, NPI, license state, phone, and an optional message. (At a Canadian pharmacy there&apos;s no NPI, and you pick your License Province instead.)</li>
        <li>Submit the request.</li>
        <li>The pharmacy reviews and approves your request.</li>
        <li>You receive an invitation email to set your password.</li>
      </ol>
      <p>
        Each pharmacy chooses what to publish, so you may see only one of the
        two: a landing page can show the catalog without the request form, or
        the request form without the catalog. If there&apos;s no form, contact the
        pharmacy directly and they&apos;ll invite you.
      </p>
    </>
  )
}

function SubmittingPrescriptionSection() {
  const { config } = usePortal()
  const providerPay = providerPayLabel(config?.use_clinic_pay_label)
  return (
    <>
      <p>
        Your portal has five tabs — <strong>New Prescription</strong>,{' '}
        <strong>Saved</strong>, <strong>History</strong>,{' '}
        <strong>Catalog</strong> and <strong>Help</strong>{' '}
        (this guide) — plus a <strong>Pharmacy Notes</strong> tab when your
        pharmacy has published notes for its providers. Start on <strong>New Prescription</strong>. The form has
        collapsible sections that show a completion badge as you fill them out.
      </p>
      <p>
        <strong>Prescribing more than one product for the same patient?</strong>{' '}
        Enter the patient and sign just once, then add up to <strong>30
        products</strong> to a single submission — ideal when a patient needs
        several compounds at once (e.g., an HRT stack, or a topical plus an
        oral). See <em>Step 2</em> below. Each product still becomes its own
        prescription, PDF, and fax, with its own reference ID.
      </p>

      <Heading>Step 1: Patient Information</Heading>
      <p className="font-medium">For human patients:</p>
      <Table
        headers={['Field', 'Required', 'Notes']}
        rows={[
          ['First Name', 'Yes', ''],
          ['Middle Name', 'No', ''],
          ['Last Name', 'Yes', ''],
          ['Date of Birth', 'Yes', 'Cannot be a future date'],
          ['Gender', 'No', 'Male, Female, Nonbinary, or Unknown. Marked with an asterisk, but it starts on Unknown, so it is always filled in.'],
          ['Phone', 'Yes', ''],
          ['Email', 'No', ''],
          ['Street Address', 'No*', 'With autocomplete'],
          ['City, State, ZIP', 'No*', ''],
          ['Allergies', 'No', 'Or check "No known allergies"'],
          ['Current Medications', 'No', 'Or check "Not taking any medications"'],
        ]}
      />
      <p className="text-sm text-gray-600">
        <strong>*</strong> If your pharmacy has enabled{' '}
        <strong>Require Patient Address</strong> (in Portal Configuration, or on
        your <strong>Override Settings</strong> page), the address fields become
        required and you can&apos;t submit a human prescription without them.
      </p>
      <p className="font-medium mt-3">For animal patients (veterinary mode):</p>
      <Table
        headers={['Field', 'Required', 'Notes']}
        rows={[
          ['Pet Name', 'Yes', ''],
          ['Species', 'Yes', 'e.g., Dog, Cat, Horse'],
          ['Date of Birth', 'Yes**', 'Enter the pet\'s date of birth, or check "Date of Birth Unknown". Cannot be a future date.'],
          ['Breed', 'No', ''],
          ['Gender', 'No', 'Male, Female, or Unknown. Defaults to Unknown.'],
          ['Weight', 'Yes', 'With unit selector (lbs or kg)'],
          ['Owner First Name', 'Yes', ''],
          ['Owner Last Name', 'Yes', ''],
          ['Owner Phone', 'Yes', ''],
          ['Owner Email', 'No', ''],
          ['Allergies', 'No', 'Or check "No known allergies"'],
          ['Current Medications', 'No', 'Or check "Not taking any medications"'],
        ]}
      />
      <p className="text-sm text-gray-600">
        <strong>**</strong> Pet date of birth works like the allergies and
        medications acknowledgements: enter the date <strong>or</strong> tick{' '}
        <strong>Date of Birth Unknown</strong> — you can&apos;t leave it blank
        without confirming it&apos;s unknown.
      </p>
      <p>
        If you&apos;ve prescribed for a patient before, you can select from{' '}
        <strong>recent patients</strong> to auto-fill their information
        (including their gender and, for pets, their date of birth).
      </p>

      <Heading>Step 1.5: Prescribing From (Multi-Address Only)</Heading>
      <p>
        If your profile has <strong>more than one practice address</strong>,
        the form shows a <strong>Prescribing From</strong> section listing
        each of your offices. Pick which office this prescription is coming
        from — your primary address is pre-selected.
      </p>
      <p>
        The selected office name and address are captured on the prescription
        and appear on the faxed PDF, so the pharmacy knows exactly which of
        your locations is ordering. The selection is snapshotted at submission
        time — past prescriptions keep their original office even if you
        later edit or remove the address.
      </p>
      <p>
        With only one address on file, this step is skipped automatically.
      </p>

      <Heading>Step 2: Select a Product</Heading>
      <p>There are two ways to find a product, side by side on the form:</p>
      <BulletList
        items={[
          <><strong>Search</strong> — type into the search box to match any product in the pharmacy&apos;s catalog by name.</>,
          <><strong>Browse</strong> — work down from category to drug to variant, if you&apos;d rather narrow by category.</>,
        ]}
      />
      <p>
        Either way, selecting a product auto-fills all prescription defaults,
        displays product notes and any medical necessity statement, and brings up
        any custom fields.
      </p>
      <p>
        Products are listed by name, and each product&apos;s strengths in
        numeric order (2.5 mg before 10 mg).
      </p>
      <p>
        You can also choose from your <strong>5 most recently prescribed products</strong>{' '}
        under <strong>Recently prescribed</strong>, for quick access.
      </p>
      <p>
        <strong>Adding multiple products:</strong> Click <strong>Add another
        product</strong> to prescribe more than one product for this patient. A
        new product section appears with its own product picker and details —
        each section is fully independent, so the quantity, days supply,
        refills, and custom-field values on one product never affect another.
        Add up to <strong>30 products</strong> per submission; at the limit the
        button is disabled with a &ldquo;Maximum of 30 prescriptions per
        submission&rdquo; note. Larger submissions take a little longer to
        send — they go to the pharmacy in batches of 10. Take an extra product back off with{' '}
        <strong>Remove</strong> on its section (the first product can&apos;t be
        removed). Patient info,
        the &ldquo;Prescribing From&rdquo; office, diagnosis codes, payment /
        delivery, and signature are entered once and apply to every product.
      </p>

      <Heading>Step 3: Prescription Details</Heading>
      <Table
        headers={['Field', 'Notes']}
        rows={[
          ['Dose Specific Directions', 'Editable. Changes are marked "Modified" on the fax.'],
          ['Quantity', 'Editable. Modifications are flagged.'],
          ['Days Supply', 'Editable. Modifications are flagged.'],
          ['Refills', 'Editable (0–11). Modifications are flagged.'],
          ['Custom Fields', 'Fill in any product-specific fields (text, number, date, dropdown, checkbox). Required ones must be filled in before you can submit — the form lists any that are missing.'],
        ]}
      />

      <Heading>Additional Information</Heading>
      <p>
        Some pharmacies ask a few questions once per order, whatever the
        product — a brand or an order reference, for example. If yours does, an{' '}
        <strong>Additional Information</strong> card appears after the product
        sections. Answer it once and it applies to every product in the
        submission. Required questions must be answered before you can submit.
      </p>

      <Heading>Step 4: Diagnosis Code (Optional)</Heading>
      <p>
        Search the ICD-10 list by code or description and pick the one that
        applies. The field holds a <strong>single</strong> diagnosis code —
        choosing another replaces the one already there.
      </p>

      <Heading>Step 5: Payment &amp; Delivery (Optional)</Heading>
      <Table
        headers={['Field', 'Options']}
        rows={[
          ['Payment Type', `Patient Pay, ${providerPay}`],
          ['Delivery Method', 'Patient Pickup, Patient Ship, Provider Pickup, Provider Ship'],
        ]}
      />
      <p>
        Your pharmacy can set the options for each of your practice addresses.
        When a single option is set for the office you&apos;re prescribing
        from, that field shows the locked value marked <em>(set by
        pharmacy)</em> instead of options — you don&apos;t need to choose. When
        several are set, you only see those options. If you change the{' '}
        <strong>Prescribing From</strong> office, the options are re-read from
        that address, so they may change, and a choice that office
        doesn&apos;t offer is cleared.
      </p>
      <p>
        Your pharmacy may also <strong>hide</strong> some or all of these fields
        (the diagnosis code, payment type, delivery method, or the whole
        section). Any field your pharmacy has hidden won&apos;t appear, and you
        can submit without it.
      </p>

      <Heading>Step 6: Electronic Signature</Heading>
      <p>
        A signature pad appears at the end of the form. If the pharmacy
        requires signatures, you must sign before submitting; otherwise signing
        is optional. Any signature you draw is included on the faxed
        prescription.
      </p>

      <Heading>Step 7: Submit</Heading>
      <p>
        Click <strong>Submit</strong>. You receive a confirmation page listing
        every product you submitted — one row per product, each with its own
        reference number — plus buttons to print the page or submit another
        prescription. If an individual product can&apos;t be processed, it&apos;s
        shown with an error message while the others still go through; one
        failure doesn&apos;t block the rest. A separate PDF is generated and
        faxed for each product.
      </p>
      <p>
        The confirmation tells you the prescription was submitted, not that the
        fax has landed. Track delivery on the <strong>History</strong> tab, where
        each prescription shows its current status.
      </p>
      <p>
        <strong>Not ready to submit?</strong> Click <strong>Save for
        later</strong> instead of <strong>Submit</strong> to set the script
        aside without sending it — no signature required. It moves to your{' '}
        <strong>Saved</strong> list, where you (or the prescriber) can reopen
        and submit it later.
      </p>
    </>
  )
}

function SavedScriptsSection() {
  return (
    <>
      <p>Navigate to <strong>Saved</strong> from the tab bar.</p>
      <p>
        A <strong>saved script</strong> is an in-progress prescription you&apos;ve
        set aside without submitting — useful when staff prepare a script and the
        prescriber signs and submits it afterward, or when you simply want to
        finish later.
      </p>
      <p>
        <strong>To save:</strong> on the Prescribe form, click <strong>Save for
        later</strong> instead of <strong>Submit</strong>. You don&apos;t need to
        complete every field or add a signature — whatever you&apos;ve entered
        (patient, product lines, diagnosis / payment / delivery, and a signature
        if you drew one) is saved together.
      </p>
      <p>The Saved list shows each draft with:</p>
      <BulletList
        items={[
          'Patient name (or animal name)',
          'A summary of the product(s) on the script',
          'When it was last saved',
          <>A <strong>Signed</strong> badge if a signature was already captured</>,
        ]}
      />
      <BulletList
        items={[
          <><strong>Continue</strong> — Reopens the script on the Prescribe form with everything prefilled exactly as you left it. The button changes to <strong>Update saved script</strong>, which re-saves the same draft in place (no duplicate). When ready, sign (if required) and <strong>Submit</strong> — the draft is removed automatically once submitted.</>,
          <><strong>Delete</strong> — Discards the saved script.</>,
        ]}
      />
      <p>
        Saved scripts are <strong>private to you</strong>, and they&apos;re not
        submissions: saving doesn&apos;t generate a PDF or fax, doesn&apos;t
        appear in History, and doesn&apos;t count toward your prescription
        allowance until you actually submit. If your catalog&apos;s defaults
        change later, a saved script keeps the values exactly as they were when
        you saved it.
      </p>
    </>
  )
}

function PrescriptionHistorySection() {
  return (
    <>
      <p>Navigate to <strong>History</strong> from the tab bar.</p>
      <p>
        Every prescription you&apos;ve submitted appears here, with its date,
        reference ID, patient, drug name and current status.
      </p>
      <BulletList
        items={[
          <><strong>Search</strong> — matches reference ID, drug name, or patient name</>,
          <><strong>From / To</strong> — restrict to a date range</>,
          <><strong>Status</strong> — narrow to Submitted, Fax Sent, Delivered, or Failed</>,
          <><strong>Sort</strong> — by date (newest/oldest) or drug name (A–Z, Z–A)</>,
          <><strong>View Fax</strong> — opens the faxed prescription PDF. It appears once the fax has been sent, so a prescription you&apos;ve just submitted won&apos;t have it yet</>,
          <><strong>Prescribe Again</strong> — Start a new prescription from this one. Choose <strong>Same Patient</strong> to copy the whole script (patient, product, directions, quantity, days supply, refills and custom field answers), ready to review and submit, or <strong>Different Patient</strong> to start with just the product and its defaults</>,
        ]}
      />
    </>
  )
}

function CatalogSection() {
  return (
    <>
      <p>
        The <strong>Catalog</strong> tab lists every product you can
        prescribe, grouped by category. If your pharmacy has given you a custom
        set of products, you see only those.
      </p>
      <p>
        Each product card shows its name, strength, product code, NDC or DIN,
        ingredients, directions, quantity, days supply and refills. Use the
        search box to find a product by name, code, ingredient or category, and{' '}
        <strong>Refresh</strong> to pick up changes your pharmacy has just made.
      </p>
      <p>
        The Catalog is for reference only. To prescribe a product, start a{' '}
        <strong>New Prescription</strong> and pick it there.
      </p>
    </>
  )
}

function PasswordManagementSection() {
  return (
    <>
      <Heading>First-Login Password Change</Heading>
      <p>
        If your pharmacy created your account with a password for you (rather
        than inviting you by email), you&apos;ll be taken to a change-password
        page the first time you sign in. Enter the pharmacy-set password as your
        current password and choose a new one — until you do, only{' '}
        <strong>Sign out</strong> is available and you can&apos;t prescribe or
        change your profile. Completing a <strong>Forgot Password</strong> reset
        also satisfies this requirement.
      </p>

      <Heading>Changing Your Password</Heading>
      <ol className="list-decimal list-inside space-y-1.5">
        <li>Go to <strong>Profile</strong>.</li>
        <li>Click <strong>Change Password</strong>.</li>
        <li>Enter your current password and your new password.</li>
        <li>Save.</li>
      </ol>

      <Heading>Forgot Your Password?</Heading>
      <ol className="list-decimal list-inside space-y-1.5">
        <li>On the login page, click <strong>Forgot Password</strong>.</li>
        <li>Enter your email address.</li>
        <li>Check your email for a reset link.</li>
        <li>Click the link and create a new password.</li>
      </ol>
      <p>
        If you use two-factor authentication, you&apos;re asked for a code from
        your authenticator app before the reset signs you in.
      </p>
      <p>
        Each pharmacy portal is a separate login. If you use the same email at
        more than one pharmacy that uses this portal, reset your password from
        that pharmacy&apos;s own portal URL — each portal keeps its own sign-in,
        so you can be logged into several at once.
      </p>

      <Heading>Signing In Without Your Pharmacy&apos;s Link</Heading>
      <p>
        You can also sign in from the main sign-in page, the one without a
        pharmacy&apos;s link. Choose <strong>Provider</strong> at the top of the
        page, then enter your email and password. You land on the New
        Prescription page of your pharmacy&apos;s portal.
      </p>
      <p>
        If your email and password work at more than one pharmacy, you see a{' '}
        <strong>Choose a pharmacy</strong> list. Pick the pharmacy you want to
        prescribe for. Only pharmacies where that password works, and where your
        account is active, are listed. Click <strong>Back</strong> to return to
        the sign-in form.
      </p>
    </>
  )
}

function ReceivingPrescriptionsSection() {
  return (
    <>
      <p>
        Prescriptions arrive via fax at your configured fax number. Each fax is a
        structured document with these sections:
      </p>

      <Heading>Pharmacy Header</Heading>
      <p>Your pharmacy name, phone, fax, NPI, and address.</p>

      <Heading>Patient Information</Heading>
      <BulletList
        items={[
          <><strong>Human:</strong> Full name, date of birth, age, gender, address, phone, email, allergies, current medications</>,
          <><strong>Animal:</strong> Pet name, species, breed, date of birth (or &ldquo;Unknown&rdquo;), gender, weight, owner name, phone, and email, allergies, medications</>,
        ]}
      />
      <p>
        Gender prints as &ldquo;Unspecified&rdquo; when it was left as Unknown.
        Dates of birth print as MM/DD/YYYY.
      </p>

      <Heading>Prescriber Information</Heading>
      <p>
        Provider name, NPI, license number and state, phone, fax, address, DEA
        number, credential type, supervising physician name and NPI (for PA/NP
        credential types), and electronic signature (if the provider signed).
      </p>

      <Heading>Prescription Details</Heading>
      <BulletList
        items={[
          'Drug name and product code (for quick lookup in your system)',
          'NDC or DIN (if applicable), ingredients (for compounds)',
          'Strength (e.g., "200mg/mL")',
          'SIG — Combined dose directions and additional directions',
          'Quantity with unit, days supply, refills',
          'Diagnosis code and medical necessity statement (if set)',
        ]}
      />
      <p>
        If the provider changed any default values (directions, quantity, days supply,
        or refills), those fields are marked <strong>MODIFIED</strong> on the fax.
      </p>

      <Heading>Additional Sections</Heading>
      <BulletList
        items={[
          <><strong>Product Notes</strong> — Read-only metadata from the product catalog</>,
          <><strong>Additional Information</strong> — The provider&apos;s responses to your product custom fields, followed by the general custom fields</>,
          <><strong>Payment &amp; Delivery</strong> — Payment type and delivery method</>,
          <><strong>Footer</strong> — Reference ID and submission time (MM/DD/YYYY HH:MM:SS UTC)</>,
          <><strong>Machine-Readable Zone</strong> — Structured text for automated pharmacy processing, including a <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm">CUSTOM_&lt;KEY&gt;</code> line for each custom field answer. Its dates stay in YYYY-MM-DD form. Left off the fax when <strong>Hide Machine-Readable Zone on Faxes</strong> is enabled in Settings</>,
        ]}
      />
    </>
  )
}

function SecuritySection({ role }: { role: Role }) {
  return (
    <>
      <p>The TJM Provider Portal is designed with healthcare privacy requirements in mind.</p>

      <Heading>Platform Security</Heading>
      <BulletList
        items={[
          'Your data is encrypted both while it travels and while it is stored',
          'Patient information is encrypted individually, not just the storage around it',
          'Signing out ends your session everywhere, and access changes take effect immediately',
          'Repeated failed sign-in and password-reset attempts are throttled',
          'Optional two-factor authentication with an authenticator app, for pharmacy teams and providers',
          'Patient health information is kept out of system records',
          'A permanent, append-only trail of every action is retained',
        ]}
      />

      <Heading>Your Responsibilities</Heading>
      {role === 'provider' ? (
        <BulletList
          items={[
            'Keep your login credentials secure and do not share them',
            'Turn on two-factor authentication from your profile',
            'Sign out when you finish on a shared or public computer',
            'Check the patient and product on each prescription before you submit it',
            'Tell your pharmacy promptly if you think someone else has used your account',
          ]}
        />
      ) : (
        <BulletList
          items={[
            'Keep your login credentials secure and do not share them',
            'Turn on two-factor authentication, especially for Admins and Managers',
            'Only invite trusted, licensed providers',
            'Disable or delete provider accounts when they should no longer have access',
            'Give each teammate the lowest role that lets them do their job, and disable them when they leave',
            'Review access requests carefully before approving',
          ]}
        />
      )}
    </>
  )
}

function FAQSection({ role }: { role: Role }) {
  const faqs: { q: string; a: string; roles: Role[] }[] = [
    {
      q: 'Can I have multiple admin accounts for my pharmacy?',
      a: 'Yes. Your pharmacy supports three team roles — Admin, Manager, and Staff — with room for 3 Admins, 10 Managers and 10 Staff. Admins and Managers invite and manage team members from the Team page in your admin portal. See Team & Roles for what each role can reach.',
      roles: ['admin'],
    },
    {
      q: 'Someone changed my role and I got signed out. Is that a bug?',
      a: 'No. Role changes apply immediately, so anyone whose role changes (or who is disabled) is signed out on the spot and asked to sign in again. Signing back in picks up the new permissions. Bear it in mind before changing a colleague\'s role while they\'re mid-task.',
      roles: ['admin'],
    },
    {
      q: 'A teammate left. Should I remove them or disable them?',
      a: 'Disable them. Once someone has accepted their invitation they can no longer be removed, so their history stays intact — a disabled account keeps its record but can\'t sign in. Removal is only for invitations that were never accepted, where it\'s called "Cancel invitation".',
      roles: ['admin'],
    },
    {
      q: 'Do we have to use two-factor authentication?',
      a: 'No. It\'s optional for everyone, and nobody is asked for a code until they turn it on themselves: your team from My Account, providers from their profile. We recommend it, especially for Admins and Managers.',
      roles: ['admin'],
    },
    {
      q: 'A teammate or provider lost their phone and can\'t get a code. What do I do?',
      a: 'An Admin or Manager can turn their two-factor off. For a teammate, use "Reset two-factor" in their row menu on the Team page. A Manager can do this only for Staff. For a provider, open their details page and click "Reset Two-Factor". They then sign in with just their password and can set two-factor up again.',
      roles: ['admin'],
    },
    {
      q: 'What if a provider forgets their password?',
      a: 'They can use the "Forgot Password" link on the login page to reset it via email.',
      roles: ['admin'],
    },
    {
      q: 'What if I need to change my fax number?',
      a: 'Contact portal@tjmlabs.com to update your fax number. New prescriptions will be sent to the updated number.',
      roles: ['admin'],
    },
    {
      q: 'Can providers submit prescriptions for products not in my catalog?',
      a: 'No. The portal only allows selection of pre-configured catalog products. If a provider needs a product that\'s not in your catalog, they should contact you directly.',
      roles: ['admin'],
    },
    {
      q: 'What if a provider modifies the directions or quantity?',
      a: 'Providers can edit prescription details before submitting. Modified fields are clearly marked on the faxed prescription.',
      roles: ['admin'],
    },
    {
      q: 'How do I know if a prescription was successfully faxed?',
      a: 'Open the Faxes page and check the prescription\'s status. Statuses are: Submitted, Fax Sent, Delivered, or Failed. If a fax fails, you can retry it from the same row, and your Admins and Managers are emailed automatically.',
      roles: ['admin'],
    },
    {
      q: 'Can I use this for controlled substances?',
      a: 'The system supports controlled substances, but you must follow your state\'s regulations and your pharmacy\'s policies.',
      roles: ['admin', 'provider'],
    },
    {
      q: 'What happens when I reach my prescription limit?',
      a: 'You\'ll see a warning at 80% usage. If you exceed your limit, overage charges apply. You can upgrade your plan tier at any time through the Billing page.',
      roles: ['admin'],
    },
    {
      q: 'Where can I see a provider\'s DEA number or practice addresses?',
      a: 'Click the provider on the Providers page to open their details. It shows their contact details, credentials (including DEA, for US pharmacies), every practice address, account status, catalog access, and any settings overridden for them.',
      roles: ['admin'],
    },
    {
      q: 'Can I restrict which products a provider sees?',
      a: 'Yes. Use the Manage Catalog option on the provider\'s menu to assign specific products.',
      roles: ['admin'],
    },
    {
      q: 'Can I apply a different setting to just one provider?',
      a: 'Yes. Open a provider from the Providers page and click "Override Settings" (or choose it from the row menu) to reach their per-provider Override Settings page. There you can take any pharmacy-wide default — signature, NPI, DEA, phone, patient address required, address management, country field, or hiding Payment & Delivery fields — and leave it on Default or set it to On or Off for that provider alone. Changes save automatically.',
      roles: ['admin'],
    },
    {
      q: 'Can I require a patient\'s address on prescriptions?',
      a: 'Yes. Turn on "Require Patient Address" under Settings → Portal Configuration to make a full patient address mandatory for human prescriptions. It\'s off by default, and you can override it per provider on their Override Settings page.',
      roles: ['admin'],
    },
    {
      q: 'Does the portal support veterinary prescriptions?',
      a: 'Yes. In Portal Settings, set your prescribing mode to "Veterinary Only" or "Both." The form will include animal-specific fields.',
      roles: ['admin', 'provider'],
    },
    {
      q: 'Can providers request access without an invitation?',
      a: 'Yes, if the Landing Page is enabled. Providers can visit the portal URL, browse the catalog, and submit an access request.',
      roles: ['admin'],
    },
    {
      q: 'How do I import products in bulk?',
      a: 'Go to your Catalog page, click Import CSV, download the template, fill it in, and upload.',
      roles: ['admin'],
    },
    {
      q: 'Can I edit lots of existing products at once?',
      a: 'Yes. On the Catalog page click Import CSV, then Download Catalog to export your products, edit them in a spreadsheet, and re-upload with "Update existing products" ticked. You get a preview of every change — including anything that will be cleared — and nothing is saved until you press Apply Changes. In this mode an empty cell clears that field, so always start from the downloaded file rather than a hand-built one.',
      roles: ['admin'],
    },
    {
      q: 'Can I delete several products at once?',
      a: 'Yes. On the Catalog page click Select, tick the products (or click Select all to pick everything matching your search), then click Delete selected. You\'ll see the full list before anything is deleted, and you must keep at least one active product.',
      roles: ['admin'],
    },
    {
      q: 'How do I rename or delete a category?',
      a: 'Go to the Categories page in the admin sidebar and use the action menu (⋮) on that category. Renaming updates every product using it. Deleting removes the tag from its products but never deletes the products themselves.',
      roles: ['admin'],
    },
    {
      q: 'Can one product be in more than one category?',
      a: 'Yes. The Categories box on the product form is a multi-select — add as many as you like, and type a new name to create a category on the spot.',
      roles: ['admin'],
    },
    {
      q: 'How do I set up a veterinarian who has no NPI or DEA?',
      a: 'Open that provider\'s Override Settings page and tick Veterinarian. Their NPI Required and DEA Required settings switch to Off immediately and stay off even if you later change your pharmacy-wide requirements. Unticking it returns both to your portal defaults.',
      roles: ['admin'],
    },
    {
      q: 'I forgot my password. What do I do?',
      a: 'On the login page, click "Forgot Password" and enter your email. You\'ll receive a link to reset your password.',
      roles: ['provider'],
    },
    {
      q: 'I lost my phone and can\'t get a two-factor code. What do I do?',
      a: 'Contact your pharmacy. An Admin or Manager there can reset your two-factor, and you then sign in with just your password. You can turn it on again from your profile.',
      roles: ['provider'],
    },
    {
      q: 'I prescribe for more than one pharmacy. Where do I sign in?',
      a: 'At each pharmacy\'s own portal URL, or on the main sign-in page: choose "Provider", sign in, and pick the pharmacy from the list.',
      roles: ['provider'],
    },
    {
      q: 'Why can\'t I access the prescription form?',
      a: 'You need to complete your profile first. Go to Profile and fill in all required fields (name, license, address, and anything else your pharmacy requires, such as NPI, DEA, or a phone number).',
      roles: ['provider'],
    },
    {
      q: 'Can I prescribe again for the same patient?',
      a: 'Yes. Go to History, find the previous prescription, and click "Prescribe Again". Choose "Same Patient" to copy the whole script, or "Different Patient" to reuse just the product.',
      roles: ['provider'],
    },
    {
      q: 'What does "Modified" mean on a prescription?',
      a: 'It means you changed a field (directions, quantity, days supply, or refills) from the pharmacy\'s default value for that product.',
      roles: ['provider'],
    },
  ]

  const filtered = faqs.filter((f) => f.roles.includes(role))

  return (
    <div className="space-y-4">
      {filtered.map((faq, i) => (
        <div key={i}>
          <p className="font-medium text-charcoal">Q: {faq.q}</p>
          <p className="text-gray-600 mt-1">A: {faq.a}</p>
        </div>
      ))}
      {role === 'admin' && (
        <p className="text-gray-500 text-sm mt-6">
          Need more help? Contact us at{' '}
          <a href="mailto:portal@tjmlabs.com" className="text-[var(--color-deep-purple)] hover:underline">
            portal@tjmlabs.com
          </a>
        </p>
      )}
    </div>
  )
}

/* --------------------------------------------------------------------------
   Main GuideContent component
   -------------------------------------------------------------------------- */

interface GuideContentProps {
  role: Role
}

export function GuideContent({ role }: GuideContentProps) {
  const visibleSections =
    role === 'both'
      ? sections
      : sections.filter((s) => s.roles.includes(role))

  // For sub-components that need a simple role, default to 'admin' when viewing both
  const simpleRole = role === 'both' ? 'admin' : role

  // Map section IDs to their content
  const sectionContent: Record<string, React.ReactNode> = {
    overview: <OverviewSection />,
    'getting-started': <GettingStartedSection role={simpleRole} />,
    dashboard: <DashboardSection />,
    'portal-settings': <PortalSettingsSection />,
    'product-catalog': <ProductCatalogSection />,
    categories: <CategoriesSection />,
    'general-custom-fields': <GeneralCustomFieldsSection />,
    'provider-management': <ProviderManagementSection />,
    'access-requests': <AccessRequestsSection />,
    prescriptions: <PrescriptionsSection />,
    team: <TeamSection />,
    billing: <BillingSection />,
    'accepting-invitation': <AcceptingInvitationSection />,
    'completing-profile': <CompletingProfileSection />,
    'requesting-access': <RequestingAccessSection />,
    'submitting-prescription': <SubmittingPrescriptionSection />,
    'saved-scripts': <SavedScriptsSection />,
    'prescription-history': <PrescriptionHistorySection />,
    catalog: <CatalogSection />,
    'password-management': <PasswordManagementSection />,
    'two-factor': <TwoFactorContent role="provider" />,
    'receiving-prescriptions': <ReceivingPrescriptionsSection />,
    security: <SecuritySection role={simpleRole} />,
    faq: <FAQSection role={role === 'both' ? 'admin' : role} />,
  }

  return (
    <div className="flex gap-8">
      {/* Sticky TOC sidebar — desktop only */}
      <nav className="hidden xl:block w-56 shrink-0">
        <div className="sticky top-6 space-y-1">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
            Contents
          </p>
          {visibleSections.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => scrollToSection(s.id)}
              className="block w-full text-left text-sm text-gray-500 hover:text-charcoal py-1 px-2 rounded hover:bg-gray-100 transition-colors"
            >
              {s.title}
            </button>
          ))}
        </div>
      </nav>

      {/* Guide sections */}
      <div className="flex-1 min-w-0 space-y-4">
        {visibleSections.map((s, i) => (
          <GuideSection
            key={s.id}
            id={s.id}
            title={s.title}
            defaultOpen={i === 0}
          >
            {sectionContent[s.id]}
          </GuideSection>
        ))}
      </div>
    </div>
  )
}
